import React, { useRef, useState } from 'react'
import axios from 'axios';
import DeleteForeverIcon from '@mui/icons-material/DeleteForever';
import VisibilityIcon from '@mui/icons-material/Visibility';
import { Box, IconButton } from '@mui/material';
import { bindActionCreators } from 'redux';
import { useDispatch, useSelector } from 'react-redux';
import { Loader } from '@egovernments/digit-ui-react-components'
import { getDocumentByFileStoreId } from '@egovernments/digit-ui-libraries/src/utils/fileType';
import { form11DocActions } from "@egovernments/digit-ui-module-core/src/redux/actions";

export default function FileUploader({
	name,
	label,
	control,
	hasLabel = true,
	isMandatory = false,
	validationMsg,
	required,
	validation,
	tab
}) {
	const { documents } = useSelector(state => state.form11);
	const dispatch = useDispatch();
	const { addForm11Doc, removeForm11Doc } = bindActionCreators(form11DocActions, dispatch);
	const tenantId = Digit.ULBService.getCurrentTenantId();
	const fileInputRef = useRef(null);
	const [isLoading, setIsLoading] = useState(false);
	const [isPreviewLoading, setIsPreviewLoading] = useState(false);
	const [showToast, setShowToast] = useState(null);

	const handleFileUpload = async (file, inputName) => {
		setIsLoading(true);
		setShowToast(null)
		fileInputRef.current.value = null;
		if (!file) {
			setShowToast({ error: true, label: "Please select a file." });
			setIsLoading(false);
			return;
		}
		if (tab === 1) {
			const allowedFormats = [".png", ".jpg", ".jpeg", ".pdf", ".JPG", ".PNG", ".JPEG", ".PDF"];
			const fileExtension = file.name.split('.').pop();
			if (!allowedFormats.includes(`.${fileExtension}`)) {
				setShowToast({ error: true, label: "Please select a file with a valid format (.png, .jpg, .jpeg, .pdf)." });
				setIsLoading(false);
				return;
			}
			const maxSizeInBytes = 10 * 1024 * 1024;
			if (file.size > maxSizeInBytes) {
				setShowToast({ error: true, label: "File size exceeds the maximum limit of 10 MB." });
				setIsLoading(false);
				return;
			}
		}
		else {
			const allowedFormats = [".pdf", ".PDF"];
			const fileExtension = file.name.split('.').pop();
			if (!allowedFormats.includes(`.${fileExtension}`)) {
				setShowToast({ error: true, label: "Please select a file in a pdf format" });
				setIsLoading(false);
				return;
			}
			const maxSizeInBytes = 30 * 1024 * 1024;
			if (file.size > maxSizeInBytes) {
				setShowToast({ error: true, label: "File size exceeds the maximum limit of 30 MB." });
				setIsLoading(false);
				return;
			}
		}
		const formData = new FormData();
		formData.append("file", file);
		formData.append("tenantId", tenantId);
		formData.append("module", "property-upload");
		formData.append("tag", "tag-property");

		try {
			const resp = await axios.post("/filestore/v1/files", formData, {});
			const filesId = resp.data.files[0];
			addForm11Doc({
				documentType: inputName,
				fileStoreId: filesId.fileStoreId,
				additionalDetails: { fileName: file.name, title: label }
			});
		} catch (error) {
			console.error(error.message);
		} finally {
			setIsLoading(false);
		}
	};
	return (
		<Box sx={{ display: 'flex', flexDirection: 'column' }}>
			<div className="inputContainer" style={{ alignItems: 'flex-start' }}>
				{hasLabel && (
					<h3 className={`label`}>
						{label}
						{isMandatory && <span className="compulsoryField"> *</span>}
					</h3>
				)}

				<Box sx={{
					border: '1px solid rgba(0, 0, 0, 0.23)',
					p: '0.5rem 0.75rem 0.375rem',
					borderRadius: '0.25rem',
					flexDirection: 'column',
					position: 'relative',
					width: '18.25vw',
					display: 'flex',
				}}>
					{isLoading ? (
						<Box
							sx={{
								justifyContent: 'center',
								position: 'absolute',
								alignItems: 'center',
								display: 'flex',
								inset: 0,
							}}
						>
							<Loader />
						</Box>
					) : null}

					<input
						type="file"
						name={name}
						onChange={(e) => handleFileUpload(e?.target?.files[0], name)}
						style={{ maxWidth: '100%' }}
						required={required && !documents?.some(doc => doc.documentType === name)}
						accept=".png, .jpg, .jpeg, .pdf"
						ref={fileInputRef}
					/>
					<small className="error">{validationMsg}</small>
					{!!(showToast?.error) && (
						<span className="errorText">{showToast.label}</span>
					)}
					{(!isLoading && documents?.length > 0) && (
						<Box sx={{
							maxWidth: '100%',
							display: 'flex',
							mb: 0.5,
							mt: 1,
						}}>
							{documents?.map(doc => {
								if (!(name in documents) && doc.documentType !== name) {
									return null; //<Box sx={{ fontSize: '0.875rem', textAlign: 'center' }}>No document uploaded</Box>
								}
								return (
									<Box
										sx={{
											justifyContent: 'space-between',
											p: '0.25rem 0 0.25rem 0.75rem',
											backgroundColor: '#eee',
											borderRadius: '0.25rem',
											wordBreak: 'break-all',
											fontSize: '0.875rem',
											alignItems: 'center',
											display: 'flex',
											lineHeight: 1,
											flex: 1,
										}}
										key={doc.fileStoreId}
									>
										{doc.additionalDetails?.fileName || doc.documentType}
										<Box sx={{ display: 'flex' }}>
											<IconButton size="small" color='info'
												onClick={() => getDocumentByFileStoreId(doc.fileStoreId, setIsPreviewLoading)}
											>
												{isPreviewLoading ? <Loader /> : <VisibilityIcon />}
											</IconButton>
											<IconButton size="small" color="error"
												onClick={() => removeForm11Doc(doc.documentType)}
											>
												<DeleteForeverIcon />
											</IconButton>
										</Box>
									</Box>
								)
							})}
						</Box>
					) || null}
				</Box>
			</div>
		</Box>
	)
}
